import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Search, Plus, Zap, Brain, Gift, Target, Book, Sparkles, ChevronDown, ChevronRight } from 'lucide-react';
import Skeleton from './ui/Skeleton.jsx';
import Modal from './ui/Modal.jsx';
import Button from './ui/Button.jsx';
import EmptyState from './ui/EmptyState.jsx';
import FormField from './ui/FormField.jsx';
import { Input, Textarea, Select } from './ui/Input.jsx';
import { useAuth } from '../contexts/AuthContext';
import { authorizedFetch } from '../utils/api';
import { toast } from './notifications';
import { haptic } from '../utils/haptics';
import SkillTapGuide from './skills/SkillTapGuide.jsx';

/**
 * 🚀 REVOLUTIONARY UNIFIED SKILL SELECTOR 🚀
 * 
 * Three-State System:
 * 1️⃣ NEUTRAL: Skill ist nicht ausgewählt (weiß)
 * 2️⃣ OFFERING: Ich biete diesen Skill an (gruen + Gift Icon). Eigenes Token `offer`:
 *    seit Okt 2026 ist success lila, diese Kategorie bleibt bewusst gruen (Heiko).
 * 3️⃣ SEEKING: Ich suche Hilfe bei diesem Skill (violett + Target Icon)
 * 
 * Mobile-First Design mit Card-basierter UI
 */

// Skill State Enum
const SKILL_STATES = {
  NEUTRAL: 'neutral',
  OFFERING: 'offering', 
  SEEKING: 'seeking'
};

// Kompakte Skill Badge für Accordion
const SkillBadge = ({ skill, state, onStateChange, searchTerm }) => {
  const isHighlighted = searchTerm && 
    skill.name.toLowerCase().includes(searchTerm.toLowerCase()) && 
    searchTerm.length >= 1;

  // Glas-Pill: Farbe + Schatten je State (kein backdrop-blur, performant)
  const baseStyle = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer active:opacity-80";

  const getBadgeProps = () => {
    switch (state) {
      case SKILL_STATES.OFFERING:
        return {
          className: `${baseStyle} bg-offer-soft text-foreground hover:bg-offer/20 glass-hairline-emerald`
        };
      case SKILL_STATES.SEEKING:
        return {
          className: `${baseStyle} bg-primary-soft text-foreground hover:bg-primary/20 glass-hairline-violet`
        };
      default:
        if (isHighlighted) {
          return {
            // Suchtreffer = Zustand -> Lila-Ring, kein drittes Farbfeld (CD Regel 1)
            className: `${baseStyle} bg-surface text-foreground ring-2 ring-primary/40`
          };
        }
        return {
          className: `${baseStyle} bg-surface text-muted-foreground hover:bg-surface-raised glass-hairline`
        };
    }
  };

  // Kompaktes Icon.
  // WICHTIG: Der Platz wird IMMER reserviert (unsichtbarer Platzhalter im Neutral-Zustand).
  // Sonst wuchs die Pille beim Auswaehlen um die Icon-Breite, und alle folgenden Badges
  // im flex-wrap sprangen/brachen um - genau das ruckelnde Verhalten aus dem App-Test.
  const getIcon = () => {
    switch (state) {
      case SKILL_STATES.OFFERING:
        return <Gift className="w-3.5 h-3.5 text-offer shrink-0" />;
      case SKILL_STATES.SEEKING:
        return <Target className="w-3.5 h-3.5 text-primary shrink-0" />;
      default:
        return <span className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />;
    }
  };

  // Text mit Highlighting
  const renderText = () => {
    if (isHighlighted && searchTerm) {
      const regex = new RegExp(`(${searchTerm})`, 'gi');
      const parts = skill.name.split(regex);
      return (
        <>
          {parts.map((part, i) => 
            regex.test(part) ? (
              <mark key={i} className="bg-warning/30 px-0.5 rounded">{part}</mark>
            ) : (
              <span key={i}>{part}</span>
            )
          )}
        </>
      );
    }
    return skill.name;
  };

  const { className, style } = getBadgeProps();

  return (
    <button
      type="button"
      className={className}
      style={style}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onStateChange(skill.id, state);
      }}
      title={`${skill.name} - ${state === SKILL_STATES.OFFERING ? 'Kann helfen' :
        state === SKILL_STATES.SEEKING ? 'Möchte lernen' : 'Tippen zum Auswählen'}`}
    >
      {getIcon()}
      <span>{renderText()}</span>

      {/* State Indicator Dot */}
      <div className={`w-2 h-2 rounded-full ${
        state === SKILL_STATES.OFFERING ? 'bg-offer' :
        state === SKILL_STATES.SEEKING ? 'bg-primary' : 'bg-muted-foreground/40'
      }`} />
    </button>
  );
};

// Accordion Category Header - Glassmorphism & Compact
const AccordionCategoryHeader = ({ name, count, totalSkills, isExpanded, onToggle, skillsPreview }) => {
  return (
    <div
      className={`rounded-xl transition-colors ${isExpanded ? 'bg-surface glass-lift-violet' : 'bg-surface hover:bg-surface-raised glass-hairline'}`}
    >
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggle();
        }}
        aria-expanded={isExpanded}
        aria-label={`${name} (${count} von ${totalSkills} ausgewählt)`}
        className="w-full p-3 rounded-xl"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center flex-1">
            <div className="text-left flex-1">
              <h3 className="font-semibold text-sm text-foreground">
                {name}
              </h3>
              <div className="flex items-center justify-between mt-0.5">
                <p className="text-xs text-muted-foreground">
                  {totalSkills} Skills
                </p>
                {count > 0 && (
                  <div
                    className="px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-soft text-foreground glass-hairline-violet"
                  >
                    {count}
                  </div>
                )}
              </div>

              {/* Skills Preview mit Farbkodierung */}
              {!isExpanded && skillsPreview.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2 max-w-xs">
                  {skillsPreview.slice(0, 3).map(skill => (
                    <span
                      key={skill.name}
                      className={`text-xs px-2 py-1 rounded-full font-medium inline-flex items-center gap-1 ${
                        skill.state === SKILL_STATES.OFFERING
                          ? 'bg-offer-soft text-foreground glass-hairline-emerald'
                          : 'bg-primary-soft text-foreground glass-hairline-violet'
                      }`}
                    >
                      {skill.state === SKILL_STATES.OFFERING ? (
                        <Gift className="w-3 h-3" />
                      ) : (
                        <Target className="w-3 h-3" />
                      )}
                      {skill.name}
                    </span>
                  ))}
                  {skillsPreview.length > 3 && (
                    <span className="text-xs text-muted-foreground bg-surface-raised px-2 py-1 rounded-full font-medium">
                      +{skillsPreview.length - 3} mehr
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Expand/Collapse Icon */}
          <div className={`ml-3 transition-transform duration-200 ${
            isExpanded ? 'rotate-180 text-primary' : 'text-faint'
          }`}>
            <ChevronDown className="w-5 h-5" />
          </div>
        </div>
      </button>
    </div>
  );
};

// Summary Card showing selected skills - Glassmorphism
const SkillSummary = ({ offeredSkills, soughtSkills, onSkillClick }) => {
  const totalSelected = offeredSkills.length + soughtSkills.length;

  // min-h: Der leere und der gefuellte Zustand sind gleich hoch. Sonst wuchs die Karte
  // beim ersten ausgewaehlten Skill und der komplette Kategorienbereich darunter sprang.
  if (totalSelected === 0) {
    return (
      <div className="bg-surface border border-dashed border-border rounded-xl p-5 text-center min-h-[7.5rem] flex flex-col justify-center">
        <Sparkles className="w-7 h-7 text-primary/60 mx-auto mb-2" />
        <h3 className="font-semibold text-sm text-foreground mb-1">Wähle deine Skills</h3>
        <p className="text-xs text-muted-foreground">Tippe auf Skills um sie auszuwählen</p>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-xl p-4 min-h-[7.5rem] glass-lift-violet">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-sm text-foreground flex items-center gap-1.5">
          <Sparkles className="w-4 h-4 text-primary" />
          Deine Skill-Auswahl
        </h3>
        <span
          className="bg-primary-soft text-foreground px-2 py-0.5 rounded-full text-xs font-bold glass-hairline-violet"
        >
          {totalSelected}
        </span>
      </div>

      {/* Offered Skills */}
      {offeredSkills.length > 0 && (
        <div className="mb-3">
          <h4 className="text-sm font-medium text-offer mb-2 flex items-center gap-1">
            <Gift className="w-4 h-4" />
            Ich kann helfen bei ({offeredSkills.length})
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {offeredSkills.map(skill => (
              <button
                type="button"
                key={`offer-${skill}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSkillClick && onSkillClick(skill, SKILL_STATES.OFFERING);
                }}
                className="inline-flex items-center px-2.5 py-1 bg-offer-soft text-foreground rounded-full text-xs font-medium hover:bg-offer/20 transition-colors glass-hairline-emerald"
              >
                {skill}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sought Skills */}
      {soughtSkills.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-1">
            <Target className="w-4 h-4" />
            Ich möchte lernen ({soughtSkills.length})
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {soughtSkills.map(skill => (
              <button
                type="button"
                key={`seek-${skill}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSkillClick && onSkillClick(skill, SKILL_STATES.SEEKING);
                }}
                className="inline-flex items-center px-2.5 py-1 bg-primary-soft text-foreground rounded-full text-xs font-medium hover:bg-primary/20 transition-colors glass-hairline-violet"
              >
                {skill}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Component
const UnifiedSkillSelector = ({ 
  offeredSkills = [], 
  soughtSkills = [], 
  onOfferedSkillsChange, 
  onSoughtSkillsChange,
  schoolId: propSchoolId 
}) => {
  const { user, getUserRole } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [skillsByCategory, setSkillsByCategory] = useState({});
  const [categories, setCategories] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Erster echter Tipp beendet die Vorführung in SkillTapGuide
  const [hasTappedSkill, setHasTappedSkill] = useState(false);

  // Skill Request Modal State
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm, setRequestForm] = useState({
    name: '',
    category: '',
    requestNote: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Convert skills arrays to skill states map
  const getSkillState = (skillName) => {
    if (offeredSkills.includes(skillName)) return SKILL_STATES.OFFERING;
    if (soughtSkills.includes(skillName)) return SKILL_STATES.SEEKING;
    return SKILL_STATES.NEUTRAL;
  };

  // Handle skill state change (Three-State Cycle)
  // Die Badges in den Kategorien liefern die Skill-ID, die Chips in der Zusammenfassung
  // dagegen den Skill-Namen (dort liegen nur Namen als Strings vor). Deshalb beides
  // akzeptieren - vorher fand die Suche per id nichts und die Chips waren funktionslos.
  const handleSkillStateChange = (skillIdOrName, currentState) => {
    const skill = Object.values(skillsByCategory)
      .flat()
      .find(s => s.id === skillIdOrName || s.name === skillIdOrName);

    if (!skill) return;

    haptic.selection(); // 📳 Picker-Tick beim Durchschalten der Skill-Zustände
    setHasTappedSkill(true);

    const skillName = skill.name;

    // Remove from both arrays first
    const newOfferedSkills = offeredSkills.filter(s => s !== skillName);
    const newSoughtSkills = soughtSkills.filter(s => s !== skillName);

    // Three-state cycle: neutral → offering → seeking → neutral
    switch (currentState) {
      case SKILL_STATES.NEUTRAL:
        // neutral → offering
        onOfferedSkillsChange([...newOfferedSkills, skillName]);
        onSoughtSkillsChange(newSoughtSkills);
        break;

      case SKILL_STATES.OFFERING:
        // offering → seeking
        onOfferedSkillsChange(newOfferedSkills);
        onSoughtSkillsChange([...newSoughtSkills, skillName]);
        break;

      case SKILL_STATES.SEEKING:
        // seeking → neutral
        onOfferedSkillsChange(newOfferedSkills);
        onSoughtSkillsChange(newSoughtSkills);
        break;
    }
  };

  // Handle Skill Request Submission
  const handleSkillRequest = async (e) => {
    e.preventDefault();

    // Validation
    if (!requestForm.name.trim()) {
      toast.error('Bitte gib einen Skill-Namen ein');
      return;
    }
    if (!requestForm.category) {
      toast.error('Bitte wähle eine Kategorie aus');
      return;
    }

    try {
      setIsSubmitting(true);
      const schoolIdToUse = propSchoolId || user?.schoolId;

      const response = await authorizedFetch('/api/v1/skills/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: requestForm.name.trim(),
          category: requestForm.category,
          requestNote: requestForm.requestNote.trim() || undefined,
          schoolId: schoolIdToUse
        })
      });

      if (response.ok) {
        toast.success('Skill-Vorschlag eingereicht. Die Admins deiner Schule prüfen ihn.');
        setShowRequestModal(false);
        setRequestForm({ name: '', category: '', requestNote: '' });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Einreichen des Skill-Vorschlags');
      }
    } catch (error) {
      console.error('Error submitting skill request:', error);
      toast.error(error.message || 'Skill-Vorschlag konnte nicht eingereicht werden');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Load skills data
  useEffect(() => {
    const loadSkills = async () => {
      try {
        setLoading(true);
        const schoolIdToUse = propSchoolId || user?.schoolId || 'demo-school';
        
        const response = await authorizedFetch(`/api/v1/skills/school/${schoolIdToUse}`);
        
        if (response.ok) {
          const skillsData = await response.json();
          setSkillsByCategory(skillsData);
          
          // Reihenfolge kommt vom Server (die Schule legt sie im Skill-Manager fest)
          const cats = Object.keys(skillsData);
          setCategories(cats);
          // Kategorien starten bewusst alle geschlossen (Übersichtlichkeit) –
          // kein Auto-Expand der ersten Kategorie mehr.
        } else {
          throw new Error('Failed to load skills');
        }
      } catch (error) {
        console.error('Error loading skills:', error);
        setError('Skills konnten nicht geladen werden');
        
        // Fallback to demo data
        const demoSkills = {
          'Schulfächer': [
            { id: 1, name: 'Mathematik' },
            { id: 2, name: 'Deutsch' },
            { id: 3, name: 'Englisch' },
            { id: 4, name: 'Physik' },
            { id: 5, name: 'Chemie' }
          ],
          'Kreativ': [
            { id: 6, name: 'Zeichnen' },
            { id: 7, name: 'Fotografie' },
            { id: 8, name: 'Design' }
          ],
          'Technik': [
            { id: 9, name: 'Programmieren' },
            { id: 10, name: 'Webdesign' }
          ]
        };
        
        setSkillsByCategory(demoSkills);
        setCategories(Object.keys(demoSkills));
        // Kategorien starten geschlossen – kein Auto-Expand.
      } finally {
        setLoading(false);
      }
    };

    loadSkills();
  }, [propSchoolId, user?.schoolId]);

  // Filter skills based on search
  const getFilteredSkills = (categorySkills) => {
    if (!searchTerm || searchTerm.length < 1) return categorySkills;
    
    return categorySkills.filter(skill =>
      skill.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Get all skills that match search across all categories
  const getAllFilteredSkills = () => {
    if (!searchTerm || searchTerm.length < 1) return [];
    
    const allSkills = Object.values(skillsByCategory).flat();
    return allSkills.filter(skill =>
      skill.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Toggle category expansion
  const toggleCategory = (categoryName) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryName)) {
      newExpanded.delete(categoryName);
    } else {
      newExpanded.add(categoryName);
    }
    setExpandedCategories(newExpanded);
  };

  // Calculate category stats
  const getCategoryStats = (categoryName) => {
    const categorySkills = skillsByCategory[categoryName] || [];
    const selectedCount = categorySkills.filter(skill => 
      getSkillState(skill.name) !== SKILL_STATES.NEUTRAL
    ).length;
    
    return {
      selectedCount,
      totalCount: categorySkills.length
    };
  };

  // Get skills preview for collapsed categories with state info
  const getSkillsPreview = (categoryName) => {
    const categorySkills = skillsByCategory[categoryName] || [];
    return categorySkills
      .filter(skill => getSkillState(skill.name) !== SKILL_STATES.NEUTRAL)
      .map(skill => ({
        name: skill.name,
        state: getSkillState(skill.name)
      }));
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-xl p-4 space-y-3 glass-hairline" aria-busy="true">
        <span className="sr-only">Skills werden geladen…</span>
        <Skeleton.Row />
        <Skeleton.Row />
        <Skeleton.Row />
      </div>
    );
  }

      {/* Skill vorschlagen — ui/Modal (natives <dialog>: Focus-Trap, Escape,
          Top-Layer). Ersetzt das frühere Portal-Modal mit eigenem Backdrop.
          Der Absenden-Button steht im Modal-Footer, also ausserhalb des <form>;
          `form="skill-request-form"` verbindet beide (HTML-Standard). */}
  const renderRequestModal = () => (
        <Modal
          fullscreenOnMobile
          open={showRequestModal}
          onClose={() => setShowRequestModal(false)}
          title={
            <span className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary flex-shrink-0" aria-hidden="true" />
              Skill vorschlagen
            </span>
          }
          size="md"
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => setShowRequestModal(false)}
                disabled={isSubmitting}
                className="flex-1 sm:flex-none"
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                form="skill-request-form"
                disabled={isSubmitting}
                className="flex-1 sm:flex-none"
              >
                {isSubmitting ? 'Wird eingereicht...' : 'Vorschlagen'}
              </Button>
            </>
          }
        >
          <p className="text-sm text-muted-foreground mb-5">
            Dein Skill ist nicht in der Liste? Schlage ihn vor!
          </p>

          <form id="skill-request-form" onSubmit={handleSkillRequest} className="space-y-4 sm:space-y-5">
            <FormField label="Skill-Name" required>
              <Input
                type="text"
                value={requestForm.name}
                onChange={(e) => setRequestForm({ ...requestForm, name: e.target.value })}
                placeholder="z.B. Ukulele spielen, 3D-Druck, ..."
                maxLength={50}
                autoFocus
              />
            </FormField>

            <FormField label="Kategorie" required>
              <Select
                value={requestForm.category}
                onChange={(e) => setRequestForm({ ...requestForm, category: e.target.value })}
              >
                <option value="">-- Kategorie wählen --</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
                <option value="Sonstiges">Sonstiges</option>
              </Select>
            </FormField>

            <FormField
              label="Begründung (optional)"
              hint={`${requestForm.requestNote.length}/200 Zeichen`}
            >
              <Textarea
                value={requestForm.requestNote}
                onChange={(e) => setRequestForm({ ...requestForm, requestNote: e.target.value })}
                placeholder="Warum ist dieser Skill wichtig für dich?"
                rows={3}
                maxLength={200}
                className="resize-none"
              />
            </FormField>

            <div className="bg-info-soft border-l-4 border-info p-3 sm:p-4 rounded-xl">
              <p className="text-sm text-foreground">
                <strong>Hinweis:</strong> Dein Vorschlag wird von einem Admin geprüft.
                Du erhältst eine Benachrichtigung, sobald er freigeschaltet wurde!
              </p>
            </div>
          </form>
        </Modal>
  );

  // Leerer Katalog: Die Schule hat noch keinen einzigen Skill angelegt. Dann gibt es
  // nichts zum Antippen — der Tipp-Hinweis und die Suche waeren irrefuehrend
  // (Pilotschule Stuttgart, 13. Sep 2026: Admin stand vor "Tippe einmal..." ohne Kacheln).
  const catalogEmpty = !error && categories.length === 0;
  const isAdmin = ['ADMIN', 'SUPERADMIN'].includes(getUserRole?.() || user?.role);

  if (catalogEmpty) {
    return (
      <div className="space-y-4" onClick={(e) => e.stopPropagation()}>
        <EmptyState
          icon={Sparkles}
          title="Der Skill-Katalog deiner Schule ist noch leer"
          text={isAdmin
            ? 'Lege im Dashboard unter „Skills" den Katalog an — per KI-Generator oder von Hand. Danach kannst du hier auswählen.'
            : 'Deine Schule hat noch keine Skills angelegt. Du kannst einen Skill vorschlagen — sobald er freigegeben ist, kannst du ihn hier auswählen.'}
          action={isAdmin ? (
            <Button size="sm" onClick={() => { window.location.href = '/dashboard/admin/skills'; }}>
              <Sparkles className="w-4 h-4" aria-hidden="true" />
              Katalog anlegen
            </Button>
          ) : (
            <Button size="sm" onClick={() => setShowRequestModal(true)}>
              <Plus className="w-4 h-4" aria-hidden="true" />
              Skill vorschlagen
            </Button>
          )}
        />
        {renderRequestModal()}
      </div>
    );
  }

  return (
    <div
      className="space-y-4"
      onClick={(e) => e.stopPropagation()}
      onSubmit={(e) => e.preventDefault()}
    >
      {/* Tipp-Anleitung als Vorführung mit echter Pille (der Satz allein wurde überlesen) */}
      <SkillTapGuide
        sampleSkill={skillsByCategory[categories[0]]?.[0]?.name}
        active={!hasTappedSkill}
      />

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-faint w-4 h-4" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full min-h-[44px] pl-10 pr-14 py-2.5 rounded-xl border border-border bg-surface focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary transition-colors text-sm glass-hairline"
          placeholder="Skills durchsuchen..."
        />
        <button
          type="button"
          onClick={() => setShowRequestModal(true)}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1.5 rounded-full bg-primary-soft hover:bg-primary/20 text-primary transition-colors"
          title="Skill fehlt? Schlag ihn vor."
          aria-label="Skill vorschlagen"
        >
          <Plus className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>

      {/* Search Results (when searching) - direkt unter Suchfeld, damit nicht von Tastatur verdeckt */}
      {searchTerm && searchTerm.length >= 1 && (
        <div>
          <div className="bg-surface rounded-xl p-4 glass-hairline">
            <h3 className="font-semibold text-sm text-foreground mb-3 flex items-center">
              <Search className="w-4 h-4 mr-2" />
              Suchergebnisse für "{searchTerm}"
            </h3>

            <div className="flex flex-wrap gap-2">
              {getAllFilteredSkills().map((skill, index) => (
                <SkillBadge
                  key={`search-${skill.id}-${index}`}
                  skill={skill}
                  state={getSkillState(skill.name)}
                  onStateChange={handleSkillStateChange}
                  searchTerm={searchTerm}
                />
              ))}
            </div>

            {getAllFilteredSkills().length === 0 && (
              <EmptyState
                size="sm"
                icon={Search}
                title={`Keine Skills gefunden für „${searchTerm}"`}
                text="Dein Skill ist nicht vorhanden?"
                action={(
                  <Button
                    size="sm"
                    onClick={() => {
                      setShowRequestModal(true);
                      setRequestForm({ ...requestForm, name: searchTerm });
                    }}
                  >
                    <Plus className="w-4 h-4" aria-hidden="true" />
                    Skill vorschlagen
                  </Button>
                )}
              />
            )}
          </div>
        </div>
      )}

      {/* Skill Summary */}
      <SkillSummary
        offeredSkills={offeredSkills}
        soughtSkills={soughtSkills}
        onSkillClick={handleSkillStateChange}
      />

      {/* Accordion Categories (nur wenn keine Suche aktiv) */}
      {!(searchTerm && searchTerm.length >= 1) && (
        <>
          <div className="space-y-3">
            {categories.map(category => {
              const stats = getCategoryStats(category);
              const isExpanded = expandedCategories.has(category);
              const skillsPreview = getSkillsPreview(category);
              
              return (
                <div key={category}>
                  <AccordionCategoryHeader
                    name={category}
                    count={stats.selectedCount}
                    totalSkills={stats.totalCount}
                    isExpanded={isExpanded}
                    onToggle={() => toggleCategory(category)}
                    skillsPreview={skillsPreview}
                  />
                  
                  {/* Animated Skills Container with Scrolling */}
                  <div
                    className={`transition-all duration-300 ease-in-out ${
                      isExpanded ? 'max-h-80 opacity-100 mt-2 overflow-y-auto' : 'max-h-0 opacity-0 overflow-hidden'
                    }`}
                    style={{
                      scrollbarWidth: 'thin',
                      scrollbarColor: 'rgb(var(--color-border)) rgb(var(--color-surface-raised))'
                    }}
                  >
                    <div className="bg-surface-raised rounded-xl p-3 glass-hairline">
                      <div className="flex flex-wrap gap-2">
                        {skillsByCategory[category]?.map((skill, index) => {
                          const skillState = getSkillState(skill.name);
                          return (
                            <SkillBadge
                              key={`${category}-${skill.id}-${index}`}
                              skill={skill}
                              state={skillState}
                              onStateChange={handleSkillStateChange}
                              searchTerm={searchTerm}
                            />
                          );
                        })}
                      </div>

                      {skillsByCategory[category]?.length === 0 && (
                        <p className="text-muted-foreground text-center py-4">Keine Skills in dieser Kategorie</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {error && (
        <div className="bg-danger-soft border border-danger/30 rounded-xl p-3 text-foreground text-sm">
          {error}
        </div>
      )}

      {renderRequestModal()}
    </div>
  );
};

UnifiedSkillSelector.propTypes = {
  offeredSkills: PropTypes.arrayOf(PropTypes.string),
  soughtSkills: PropTypes.arrayOf(PropTypes.string),
  onOfferedSkillsChange: PropTypes.func.isRequired,
  onSoughtSkillsChange: PropTypes.func.isRequired,
  schoolId: PropTypes.string
};

export default UnifiedSkillSelector;